import { CRM_API_BASE } from "@/lib/config"
import { httpJson } from "@/lib/http"

export type CreateInternalClientUserPayload = {
  email: string
  role: "client"
  company_name: string
  name_first: string
  name_last: string
  phone: string | null
  password: string
  password_confirm: string
}

export function buildCreateInternalClientUserPayload(params: {
  email: string
  companyName: string
  firstName: string | null
  lastName: string | null
  phone: string | null
  password: string
  passwordConfirm: string
}): CreateInternalClientUserPayload {
  return {
    email: params.email.trim(),
    role: "client",
    company_name: params.companyName.trim(),
    name_first: (params.firstName ?? "").trim(),
    name_last: (params.lastName ?? "").trim(),
    phone: params.phone?.trim() ? params.phone.trim() : null,
    password: params.password,
    password_confirm: params.passwordConfirm,
  }
}

function parseCreatedUserId(data: unknown): number | null {
  if (!data || typeof data !== "object") return null
  const o = data as Record<string, unknown>
  const nested = o.data
  const raw = o.id ?? (nested && typeof nested === "object" ? (nested as Record<string, unknown>).id : undefined)
  if (raw == null || raw === "") return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

/** Best-effort user pk from a list/detail JSON row (handles id, pk, hyperlinked url, etc.). */
export function extractIdFromUserRow(r: unknown): number | null {
  if (r == null || typeof r !== "object") return null
  const o = r as Record<string, unknown>
  for (const key of ["id", "pk", "user_id", "user"]) {
    const v = o[key]
    if (typeof v === "number" && Number.isFinite(v) && v > 0) return v
    if (typeof v === "string" && /^\d+$/.test(v)) {
      const n = Number.parseInt(v, 10)
      if (Number.isFinite(n) && n > 0) return n
    }
  }
  const url = o.url
  if (typeof url === "string") {
    const m = url.match(/\/user\/(\d+)\/?(?:\?|$)/i) ?? url.match(/\/(\d+)\/?$/)
    if (m) {
      const n = Number.parseInt(m[1], 10)
      if (Number.isFinite(n) && n > 0) return n
    }
  }
  return null
}

function collectUserListRows(data: unknown): unknown[] {
  if (!data || typeof data !== "object") return []
  const d = data as Record<string, unknown>
  if (Array.isArray(d.results)) return d.results
  if (Array.isArray(d.data)) return d.data
  if (Array.isArray(data)) return data
  return []
}

/** Normalize GET /user/ list payload (DRF paginated `{ count, results }` or a plain array). */
export function normalizeUserListPayload(data: unknown): { rows: unknown[]; count: number } {
  if (!data || typeof data !== "object") return { rows: [], count: 0 }
  if (Array.isArray(data)) return { rows: data, count: data.length }
  const d = data as Record<string, unknown>
  const rows = collectUserListRows(data)
  const count = typeof d.count === "number" && Number.isFinite(d.count) ? d.count : rows.length
  return { rows, count }
}

const fetchUserIdByEmailInflight = new Map<string, Promise<number | null>>()

/** Resolve user primary key from one GET /user/?email=… (single request; dedupes concurrent callers). */
export async function fetchUserIdByEmail(email: string, token: string | null): Promise<number | null> {
  const normalized = email.trim().toLowerCase()
  if (!normalized) return null

  const dedupeKey = `${normalized}|${token ?? ""}`
  const existing = fetchUserIdByEmailInflight.get(dedupeKey)
  if (existing) return existing

  const run = (async (): Promise<number | null> => {
    const headers: HeadersInit = {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }
    const params = new URLSearchParams({
      email: email.trim(),
      limit: "50",
      offset: "0",
    })
    const res = await fetch(`${CRM_API_BASE}/user/?${params.toString()}`, { headers })
    const data = await res.json().catch(() => null)
    if (!res.ok || !data) return null
    const rows = collectUserListRows(data)
    const match = rows.find((row) => {
      if (!row || typeof row !== "object") return false
      const em = (row as { email?: string }).email
      return (em ?? "").trim().toLowerCase() === normalized
    })
    if (!match) return null
    return extractIdFromUserRow(match)
  })()

  fetchUserIdByEmailInflight.set(dedupeKey, run)
  try {
    return await run
  } finally {
    fetchUserIdByEmailInflight.delete(dedupeKey)
  }
}

/** Payload for POST /user/ (internal dashboard create). */
export type PostCreateUserPayload = {
  email: string
  name_first: string
  name_last: string
  password: string
  password_confirm: string
  role: string
  /** Required for client role when not inferring from a linked Client record. */
  company_name?: string | null
  phone?: string | null
  is_active?: boolean
}

function buildPostUserBody(payload: PostCreateUserPayload): Record<string, unknown> {
  const body: Record<string, unknown> = {
    email: payload.email.trim().toLowerCase(),
    name_first: payload.name_first.trim(),
    name_last: payload.name_last.trim(),
    password: payload.password,
    password_confirm: payload.password_confirm,
    role: payload.role,
  }
  const phoneTrim = payload.phone != null ? String(payload.phone).trim() : ""
  body.phone = phoneTrim === "" ? null : phoneTrim

  const companyTrim = payload.company_name != null ? String(payload.company_name).trim() : ""
  if (payload.role === "client") {
    body.company_name = companyTrim
  } else if (companyTrim !== "") {
    body.company_name = companyTrim
  }

  if (payload.is_active !== undefined) body.is_active = payload.is_active

  return body
}

/** POST /user/ — internal staff only (Bearer). */
export async function postCreateUser(payload: PostCreateUserPayload, token: string | null): Promise<{ id: number }> {
  const res = await fetch(`${CRM_API_BASE}/user/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(buildPostUserBody(payload)),
  })

  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${JSON.stringify(data)}`)
  }

  let id = parseCreatedUserId(data)
  if (id == null) {
    id = await fetchUserIdByEmail(payload.email, token)
  }
  if (id == null) {
    throw new Error(
      "User create response missing id. Deploy backend with UserSerializer including id, or ensure GET /user/?email=… returns the new user.",
    )
  }
  return { id }
}

/** POST /user/ for client portal user (same endpoint; role fixed to client). */
export async function postInternalClientUser(
  payload: CreateInternalClientUserPayload,
  token: string | null,
): Promise<{ id: number }> {
  return postCreateUser(
    {
      email: payload.email,
      name_first: payload.name_first,
      name_last: payload.name_last,
      password: payload.password,
      password_confirm: payload.password_confirm,
      role: "client",
      company_name: payload.company_name,
      phone: payload.phone,
    },
    token,
  )
}

export function extractUserIdsFromClientUsers(users: unknown): number[] {
  if (!Array.isArray(users)) return []
  const ids: number[] = []
  for (const u of users) {
    if (typeof u === "number" && Number.isFinite(u) && u > 0) {
      ids.push(u)
      continue
    }
    if (u && typeof u === "object" && "id" in u) {
      const id = Number((u as { id: unknown }).id)
      if (Number.isFinite(id) && id > 0) ids.push(id)
    }
  }
  return ids
}

/** Append a user id to an org’s `users` M2M (PATCH).
 *  Pass `existingUserIds` to skip the GET. */
export async function patchOrgAppendUser(
  orgId: number,
  newUserId: number,
  existingUserIds?: number[],
): Promise<void> {
  let existing: number[]
  if (existingUserIds !== undefined) {
    existing = existingUserIds
  } else {
    const org = await httpJson<Record<string, unknown>>(`${CRM_API_BASE}/org/${orgId}/`)
    existing = extractUserIdsFromClientUsers(org.users)
  }
  const merged = [...new Set([...existing, newUserId])]
  await httpJson(`${CRM_API_BASE}/org/${orgId}/`, {
    method: "PATCH",
    body: JSON.stringify({ users: merged }),
  })
}

/** Append a user id to a client’s `users` M2M (PATCH).
 *  Pass `existingUserIds` to skip the GET (use when you already have the list from a prior fetch). */
export async function patchClientAppendUser(
  clientId: number,
  newUserId: number,
  existingUserIds?: number[],
): Promise<void> {
  let existing: number[]
  if (existingUserIds !== undefined) {
    existing = existingUserIds
  } else {
    const client = await httpJson<Record<string, unknown>>(`${CRM_API_BASE}/client/${clientId}/?include_archived=true`)
    existing = extractUserIdsFromClientUsers(client.users)
  }
  const merged = [...new Set([...existing, newUserId])]
  await httpJson(`${CRM_API_BASE}/client/${clientId}/`, {
    method: "PATCH",
    body: JSON.stringify({ users: merged }),
  })
}

